import React from "react";
import {
    Box,
    Button,
    Typography,
    Grid,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Paper,
    FormHelperText,
    Chip,
} from "@mui/material";
import {
    Add as AddIcon,
    Close as CloseIcon,
    CloudUpload as UploadIcon,
} from "@mui/icons-material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import { useTranslation } from "react-i18next";
import config from "../../config";
import { useDevicePhotoManager } from "../../hooks/useDevicePhotoManager ";

export default function InstalledPhotos({
    deviceId,
    photos: propPhotos,
    setPhotos: setPropPhotos,
    isEditing,
    onComplete,
    orgId,
    onSubmit,
    onCancel,
    isDetailsFormValid,
    triggerFormValidation,
    hideCancelButton = false,
    showDeleteButton = false,
    onDelete,
    saveButtonText,
    isEditMode,
    disabled = false,
    submitDisabled = disabled,
}) {
    const { t } = useTranslation();

    const {
        photos,
        isLoading,
        isSubmitting,
        alert,
        errors,
        touched,
        confirmModal,
        setAlert,
        setTouched,
        canAddPhoto,
        handleAddPhoto,
        handleRemovePhotoBox,
        handleRemovePhoto,
        handleCancelRemove,
        handleConfirmRemove,
        handlePhotoUpload,
        handleDownload,
        handleSave,
    } = useDevicePhotoManager({
        deviceId,
        photos: propPhotos,
        setPhotos: setPropPhotos,
        isEditing,
        orgId,
        config,
        maxPhotos: 6,
        onSubmit,
        onComplete,
    });

    const triggerFileInput = (photoId) => {
        const fileInput = document.getElementById(`photo-upload-${photoId}`);
        if (fileInput) {
            fileInput.click();
        }
    };

    const errorTextStyles = {
        color: "#d32f2f",
        fontSize: "0.75rem",
        mt: 0.5,
        textAlign: 'center',
    };

    const renderPhotoPreview = (photo) => {
        if (!photo.preview) return null;

        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                }}
            >
                <img
                    src={photo.preview}
                    alt={`Installation photo ${photo.photoNumber}`}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block",
                        margin: "0 auto",
                    }}
                    onError={(e) => {
                        console.error('Error loading photo:', photo.preview);
                        e.target.style.display = 'none';
                    }}
                />

                {photo.isUploaded && !photo.needsReplacement && (
                    <Typography
                        variant="caption"
                        sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            backgroundColor: "rgba(255,255,255,0.8)",
                            padding: "4px 8px",
                            color: "green",
                            borderRadius: "4px",
                        }}
                    >
                        ✓ {t("Uploaded")}
                    </Typography>
                )}
                {photo.needsReplacement && (
                    <Typography
                        variant="caption"
                        sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            backgroundColor: "rgba(255,255,255,0.8)",
                            padding: "4px 8px",
                            color: "orange",
                            borderRadius: "4px",
                        }}
                    >
                        {t("Pending replacement")}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                padding: { xs: "20px", md: "40px" },
                margin: { xs: "0px 20px", md: "0px 40px" },
                backgroundColor: isEditMode ? "#E8EAEE" : "#FFFFFF",
                // border: "1px solid #A5DCF3",
                borderRadius: "10px",
                // boxShadow: "-2px 2px 5px 0px #00000040",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    mb: 4,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, width: '100%' }}>
                    <Paper
                        sx={{
                            height: 52,
                            width: '100%',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            backgroundColor: '#647490',
                            boxShadow: '-2px 2px 5px 0px #00000040',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="h6"
                            color="white"
                            sx={{ fontSize: { xs: '18px', md: '20px' } }}
                        >
                            {t('Installed Device Photos with Vehicle')}
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                    }}
                >
                    <CircularProgress size={48} />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {photos.map((photo, index) => (
                        <Grid item xs={12} sm={4} key={photo.id}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    border: "1px solid #183059",
                                    borderRadius: "8px",
                                    padding: 2,
                                    position: "relative",
                                }}
                            >
                                {/* Photo Number Chip */}
                                {/* <Chip
                                    label={`Photo ${photo.photoNumber}`}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        backgroundColor: "#1976D2",
                                        color: "white",
                                        fontWeight: "bold",
                                        zIndex: 2,
                                    }}
                                /> */}

                                {/* Remove box button (X) in top right corner for empty slots */}
                                {photos.length > 1 && !photo.preview && (
                                    <IconButton
                                        onClick={() => handleRemovePhotoBox(index)}
                                        disabled={isSubmitting || disabled}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            borderRadius: 2,
                                            border: "1px solid #ccc",
                                            width: 32,
                                            height: 32,
                                            backgroundColor: "#fff",
                                            "&:hover": {
                                                backgroundColor: "#f5f5f5",
                                            },
                                            zIndex: 2,
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16, color: "#F44336" }} />
                                    </IconButton>
                                )}

                                {/* Download button for uploaded photos */}
                                {photo.preview && (
                                    <IconButton
                                        onClick={() => handleDownload(photo)}
                                        disabled={isSubmitting || disabled}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            borderRadius: 2,
                                            border: "1px solid #ccc",
                                            width: 32,
                                            height: 32,
                                            backgroundColor: "#fff",
                                            "&:hover": {
                                                backgroundColor: "#f5f5f5",
                                            },
                                            zIndex: 2,
                                        }}
                                    >
                                        <ArrowDownwardOutlinedIcon sx={{ fontSize: 16, color: "#1976D2" }} />
                                    </IconButton>
                                )}

                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "300px",
                                        border: errors[photo.id]?.file
                                            ? "1px dashed #d32f2f"
                                            : "1px solid #183059",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        overflow: "hidden",
                                        mb: 1,
                                        mt: 2, // Add margin top to account for the photo number chip
                                    }}
                                >
                                    {photo.preview ? (
                                        renderPhotoPreview(photo)
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                p: 3,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <AddIcon sx={{ fontSize: 32, mb: 1 }} />
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {t("Installation Photo")} {photo.photoNumber}
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    borderRadius: "8px",
                                                    textTransform: "none",
                                                    backgroundColor: "#20A8E0",
                                                    "&:hover": { backgroundColor: "#1565C0" },
                                                    py: 1,
                                                    px: 3,
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    fontSize: "14px",
                                                }}
                                                startIcon={<UploadIcon />}
                                                onClick={() => triggerFileInput(photo.id)}
                                                disabled={isSubmitting || disabled}
                                            >
                                                {t("Upload")}
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ mt: 2 }}
                                            >
                                                {t("Supported formats: JPG, JPEG, PNG (Max 10MB)")}
                                            </Typography>
                                            <input
                                                id={`photo-upload-${photo.id}`}
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png"
                                                hidden
                                                onChange={(e) => handlePhotoUpload(photo.id, e)}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Show filename for uploaded photos */}
                                {photo.preview && photo.fileName && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            textAlign: "center",
                                            color: "text.secondary",
                                            mb: 1,
                                            fontSize: "0.75rem",
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={photo.fileName}
                                    >
                                        {photo.fileName}
                                    </Typography>
                                )}

                                {photo.preview && (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: 1,
                                            mb: 1,
                                            mt: 1,
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            sx={{
                                                borderRadius: "8px",
                                                textTransform: "none",
                                                backgroundColor: "#F44336",
                                                "&:hover": { backgroundColor: "#D32F2F" },
                                                py: 1.5,
                                                px: 3,
                                                color: "#fff",
                                                fontWeight: "bold",
                                                fontSize: "16px",
                                                minWidth: "120px",
                                            }}
                                            startIcon={<CloseIcon sx={{ fontSize: "16px" }} />}
                                            onClick={() => handleRemovePhoto(photo.id)}
                                            disabled={isSubmitting || disabled}
                                        >
                                            {t("Remove")}
                                        </Button>
                                        
                                        {/* Replace button for existing photos */}
                                        {/* {photo.isExisting && (
                                            <Button
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: "8px",
                                                    textTransform: "none",
                                                    borderColor: "#1976D2",
                                                    color: "#1976D2",
                                                    "&:hover": { 
                                                        borderColor: "#1565C0",
                                                        backgroundColor: "#E3F2FD" 
                                                    },
                                                    py: 1.5,
                                                    px: 3,
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    minWidth: "120px",
                                                }}
                                                startIcon={<ChangeCircleOutlinedIcon sx={{ fontSize: "16px" }} />}
                                                onClick={() => triggerFileInput(photo.id)}
                                                disabled={isSubmitting || disabled}
                                            >
                                                {t("Replace")}
                                            </Button>
                                        )} */}
                                    </Box>
                                )}

                                {touched[photo.id]?.file && errors[photo.id]?.file && (
                                    <FormHelperText sx={errorTextStyles}>
                                        {errors[photo.id].file}
                                    </FormHelperText>
                                )}
                            </Box>
                        </Grid>
                    ))}

                    {canAddPhoto() && (
                        <Grid item xs={12} sm={4}>
                            <Box
                                sx={{
                                    height: 350,
                                    border: "2px dashed #1976D2",
                                    borderRadius: "8px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#E3F2FD",
                                    cursor: "pointer",
                                    "&:hover": {
                                        backgroundColor: "#BBDEFB",
                                    },
                                }}
                                onClick={disabled ? undefined : handleAddPhoto}
                            >
                                <Box sx={{ textAlign: "center" }}>
                                    <AddIcon sx={{ fontSize: 48, color: "#1976D2", mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: "#1976D2" }}>
                                        {t("Add New Photo")}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ mt: 1, color: "#1976D2" }}
                                    >
                                        {`${photos.length}/6 ${t('photos')}`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Form Action Buttons */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginTop: "40px",
                }}
            >
                {!hideCancelButton && (
                    <Button
                        onClick={onCancel}
                        disabled={isSubmitting || disabled}
                        sx={{
                            width: 120,
                            fontSize: "20px",
                            color: "#1F242E",
                            height: "55px",
                            borderRadius: "8px",
                            textTransform: "none",
                            padding: "8px 24px",
                            background: "#E0E0E0",
                            border: "1px solid #414958",
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: "#D0D0D0",
                            },
                            "&:disabled": {
                                background: "#F5F5F5",
                                color: "#A0A0A0",
                                border: "1px solid #E0E0E0",
                            },
                        }}
                    >
                        {t("Cancel")}
                    </Button>
                )}
                {showDeleteButton && (
                    <Button
                        onClick={onDelete}
                        disabled={isSubmitting || disabled}
                        sx={{
                            width: 120,
                            fontSize: "20px",
                            color: "#F20000",
                            height: "55px",
                            borderRadius: "8px",
                            textTransform: "none",
                            padding: "8px 24px",
                            background: "#FFFFFF",
                            border: "1px solid #F20000",
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: "#FFF0F0",
                            },
                            "&:disabled": {
                                background: "#F5F5F5",
                                color: "#A0A0A0",
                                border: "1px solid #E0E0E0",
                            },
                        }}
                    >
                        {t("Delete")}
                    </Button>
                )}
                <Button
                    onClick={() => handleSave(isDetailsFormValid, triggerFormValidation)}
                    disabled={isSubmitting || submitDisabled || !isDetailsFormValid}
                    sx={{
                        width: 120,
                        height: "55px",
                        fontSize: "20px",
                        color: "#1F242E",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "8px 24px",
                        background: "#F9E719",
                        border: "1px solid #414958",
                        fontWeight: 600,
                        "&:hover": {
                            backgroundColor: "#f1de16ff",
                        },
                        "&:disabled": {
                            background: "#E0E0E0",
                            color: "#A0A0A0",
                            border: "1px solid #A0A0A0",
                        },
                    }}
                >
                    {isSubmitting ? `${t("Saving")}...` : saveButtonText || t("Save")}
                </Button>
            </Box>

            {/* Snackbar for alerts */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmModal.open}
                onClose={handleCancelRemove}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        boxShadow: "-2px 2px 5px 0px #00000040",
                    },
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{ bgcolor: "#F2F2F3", color: "#414958" }}
                >
                    {t("Confirm Photo Removal")}
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 1 }}>
                    <DialogContentText id="alert-dialog-description">
                        {t(
                            "Are you sure you want to remove this photo? This action cannot be undone."
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCancelRemove}
                        sx={{
                            color: "#1F242E",
                            borderRadius: "8px",
                            textTransform: "none",
                            padding: "8px 16px",
                            background: "#F2F2F3",
                            border: "1px solid #414958",
                        }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={handleConfirmRemove}
                        sx={{
                            color: "#FFFFFF",
                            borderRadius: "8px",
                            textTransform: "none",
                            padding: "8px 16px",
                            background: "#d32f2f",
                            "&:hover": { background: "#b71c1c" },
                        }}
                        autoFocus
                    >
                        {t("Remove")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
